import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ErrorBoundary } from "./ErrorBoundary";
import { useAuth } from "@/state/auth";
import { P, type Permission } from "@/lib/permissions";
import { Button, cx } from "./ui";
import { initials } from "@/lib/format";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { LogoMark } from "./Logo";

interface NavItem {
  to: string;
  label: string;
  permission?: Permission;
  end?: boolean;
}

interface NavSection {
  title?: string;
  items: NavItem[];
}

/**
 * Navigation is grouped by who the work belongs to, not by feature.
 *
 * A merchant sees one group. A dispatcher sees two. A super admin sees three,
 * and the boundary between them is visible in the interface, which matters:
 * an operations manager clicking into a customer's account should feel like
 * crossing a line, because it is one.
 *
 * Every item is gated on a permission from /v1/me, so the menu can never offer
 * something the API will refuse.
 */
const SECTIONS: NavSection[] = [
  {
    items: [
      { to: "/app", label: "Overview", end: true, permission: P.viewShipments },
      { to: "/app/shipments", label: "Shipments", permission: P.viewShipments },
      { to: "/app/rates", label: "Rate calculator", permission: P.quote },
      { to: "/app/addresses", label: "Address book", permission: P.viewAddresses },
      { to: "/app/wallet", label: "Wallet", permission: P.viewBilling },
      { to: "/app/billing", label: "Billing", permission: P.viewBilling },
      { to: "/app/communications", label: "Communications", permission: P.viewShipments },
    ],
  },
  {
    title: "Operations",
    items: [
      { to: "/app/ops", label: "Control room", permission: P.opsDashboard },
      { to: "/app/ops/dispatch", label: "Dispatch", permission: P.opsFleet },
      { to: "/app/ops/accounts", label: "Merchants", permission: P.opsAccounts },
    ],
  },
  {
    title: "Platform",
    items: [
      { to: "/app/admin", label: "Platform overview", permission: P.platformDashboard },
      { to: "/app/admin/accounts", label: "Accounts", permission: P.platformAccounts },
      { to: "/app/admin/users", label: "Users", permission: P.platformUsers },
      { to: "/app/admin/roles", label: "Roles", permission: P.platformRoles },
      { to: "/app/admin/service-levels", label: "Service levels", permission: P.platformSettings },
      { to: "/app/admin/zones", label: "Zones & surcharges", permission: P.platformSettings },
      { to: "/app/admin/rate-cards", label: "Rate cards", permission: P.platformSettings },
      { to: "/app/admin/payments", label: "Payments", permission: P.platformBilling },
      { to: "/app/admin/settings", label: "Carrier settings", permission: P.platformSettings },
      { to: "/app/admin/notifications", label: "Notifications", permission: P.platformSettings },
      { to: "/app/admin/webhooks", label: "Webhooks", permission: P.platformSettings },
    ],
  },
  {
    title: "Account",
    items: [
      { to: "/app/team", label: "Team", permission: P.viewUsers },
      { to: "/app/api-keys", label: "Developers", permission: P.manageApiKey },
      { to: "/app/webhooks", label: "Webhooks", permission: P.manageApiKey },
      { to: "/app/settings", label: "Settings", permission: P.viewSettings },
    ],
  },
];

export function Shell() {
  const { account, user, roleLabel, can, signOut, impersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Closed by arriving somewhere, not by the link's own handler: that covers
  // every way the route can change, including the back button.
  useEffect(() => setMenuOpen(false), [location.pathname]);

  const sections = SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => !item.permission || can(item.permission)),
  })).filter((section) => section.items.length > 0);

  return (
    <div className="min-h-dvh bg-ink-50">
      {impersonating && <ImpersonationBanner />}

      <div className="flex">
        <aside className="sticky top-0 hidden h-dvh w-sidebar shrink-0 flex-col border-r border-ink-200 bg-white lg:flex">
          <div className="flex h-16 items-center gap-2 px-5">
            <LogoMark className="h-8 w-8" />
            <span className="text-lg font-bold tracking-tight">WeShip</span>
          </div>

          <nav className="flex-1 overflow-y-auto px-3 pb-4">
            {sections.map((section, index) => (
              <div key={section.title ?? index} className="mb-5">
                {section.title && (
                  <p className="mb-1.5 px-3 text-2xs font-bold uppercase text-ink-400">{section.title}</p>
                )}
                <ul className="space-y-0.5">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        end={item.end}
                        className={({ isActive }) =>
                          cx(
                            "flex h-10 items-center rounded-lg px-3 text-sm font-medium transition",
                            isActive
                              ? "bg-brand-soft text-brand"
                              : "text-ink-600 hover:bg-ink-100 hover:text-ink-900",
                          )
                        }
                      >
                        {item.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </nav>

          <div className="border-t border-ink-200 p-3">
            <div className="flex items-center gap-3 rounded-xl bg-ink-50 p-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-ink-900 text-xs font-bold text-white">
                {initials(user?.first_name, user?.last_name)}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">
                  {user ? `${user.first_name} ${user.last_name}` : account?.legal_name}
                </p>
                <p className="truncate text-xs text-ink-500">{roleLabel}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" className="mt-1 w-full justify-start" onClick={signOut}>
              Sign out
            </Button>
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-10 flex h-16 items-center gap-4 border-b border-ink-200 bg-white/95 px-4 backdrop-blur lg:px-8">
            <button
              type="button"
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="ws-mobile-nav"
              aria-label={menuOpen ? "Close the menu" : "Open the menu"}
              className="-ml-1 grid h-10 w-10 shrink-0 place-items-center rounded-lg text-ink-700 hover:bg-ink-100 lg:hidden"
            >
              {/* Two paths rather than an icon font: the menu is the first thing
                  a phone user touches and it must not wait on a download. */}
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                {menuOpen ? (
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                ) : (
                  <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
                )}
              </svg>
            </button>

            <NavLink to="/app" className="flex items-center gap-2 text-lg font-bold tracking-tight lg:hidden">
              <LogoMark className="h-7 w-7" />
              WeShip
            </NavLink>

            <div className="min-w-0 flex-1 truncate text-sm text-ink-500">
              {account?.trading_name || account?.legal_name}
            </div>

            {can(P.book) && <Button onClick={() => navigate("/app/book")}>Book a shipment</Button>}
          </header>

          {/* The same navigation on a phone, as a panel rather than a strip.
              It used to be one horizontally scrolling row of every item
              flattened together: for a super admin that measured 2.4 metres at
              a 390px viewport, so most of the menu could not be seen, the
              grouping the sidebar has was lost, and any sideways swipe near the
              top of the page dragged it -- which is what made the whole app
              feel like it scrolled sideways. */}
          {menuOpen && (
            <nav
              id="ws-mobile-nav"
              className="max-h-[70dvh] overflow-y-auto border-b border-ink-200 bg-white px-4 py-3 lg:hidden"
            >
              {sections.map((section, index) => (
                <div key={section.title ?? index} className="mb-4 last:mb-0">
                  {section.title && (
                    <p className="mb-1.5 text-2xs font-bold uppercase text-ink-400">{section.title}</p>
                  )}
                  <ul className="space-y-0.5">
                    {section.items.map((item) => (
                      <li key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.end}
                          className={({ isActive }) =>
                            cx(
                              // A 44px row: the smallest target a thumb hits
                              // reliably, which a 1.5-unit pill was not.
                              "flex h-11 items-center rounded-lg px-3 text-sm font-medium",
                              isActive ? "bg-brand-soft text-brand" : "text-ink-700",
                            )
                          }
                        >
                          {item.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
              <div className="mt-3 border-t border-ink-200 pt-3">
                <p className="px-3 text-xs text-ink-500">
                  {user ? `${user.first_name} ${user.last_name}` : account?.legal_name} · {roleLabel}
                </p>
                <Button variant="ghost" size="sm" className="mt-1 w-full justify-start" onClick={signOut}>
                  Sign out
                </Button>
              </div>
            </nav>
          )}

          <main className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
            {/* Around the page only, so a crash leaves the navigation standing
                and the next click still works. */}
            <ErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>
      </div>
    </div>
  );
}
